import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Youtube, DollarSign, GraduationCap, TrendingUp, Loader2, Compass, Film, ExternalLink } from 'lucide-react';
import { getCareerBySlug } from '../services/careerService';
import { appendUtm } from '../services/utm';
import { normalizeYouTube } from '../services/youtube';
import { isValidYouTubeId } from '../services/videoValidate';
import { PLACEHOLDER_IMAGES, SITE_URL, toPublicUrl, getRemoteFallback } from '../constants';
import { Career } from '../types';
import Button from '../components/Button';
import SEO from '../components/SEO';
import SmartImage from '../components/SmartImage';
import { track } from '../services/track';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-2xl font-heading font-semibold text-brand-white mb-6 flex items-center">
    <div className="w-1 h-6 bg-brand-cyan rounded-full mr-3"></div>
    {children}
  </h2>
);

const CareerDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [career, setCareer] = useState<Career | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const v = (import.meta as any).env?.VITE_BUILD_ID || '';
  const withV = (u: string) => (u && v && !u.startsWith('http') ? `${u}${u.includes('?') ? '&' : '?'}v=${v}` : u);

  useEffect(() => {
    setIsLoading(true);
    if (slug) {
      getCareerBySlug(slug).then(data => {
        setCareer(data || null);
        setIsLoading(false);
        if (data) track('career_open', { slug: data.slug });
      }).catch(err => {
        console.error("Failed to load career:", err);
        setCareer(null);
        setIsLoading(false);
      });
    }
  }, [slug]);

  const heroSrc = useMemo(() => {
    if (!career) return "";
    return toPublicUrl(career.images?.hero) || toPublicUrl(`/images/careers/${career.slug}/hero.webp`);
  }, [career]);

  const sidebarSrc = useMemo(() => {
    if (!career) return "";
    return toPublicUrl(career.images?.sidebar) || toPublicUrl(`/images/careers/${career.slug}/sidebar.webp`);
  }, [career]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-brand-cyan animate-spin" />
      </div>
    );
  }

  if (!career) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center text-center px-4">
        <Compass className="w-16 h-16 text-brand-cyan mb-6 animate-pulse" />
        <h1 className="text-3xl font-heading font-bold text-brand-white mb-4">Path Not Found</h1>
        <p className="text-brand-grey2 mb-8 max-w-md">We couldn't find the career details for "{slug}". The trail might have gone cold.</p>
        <Link to="/careers">
          <Button>Explore Other Careers</Button>
        </Link>
      </div>
    );
  }
  
  const ogImage = heroSrc?.startsWith('http') 
    ? heroSrc 
    : `${SITE_URL}${heroSrc}`;

  const { youtubeId, watchUrl, embedUrl } = normalizeYouTube(career.video?.youtubeUrl || '');
  const hasValidId = isValidYouTubeId(youtubeId);
  const utmWatchUrl = watchUrl ? appendUtm(watchUrl) : null;

  const handleYoutubeClick = () => {
    if (youtubeId) track('youtube_click', { slug: career.slug, youtubeId });
  };

  return (
    <div className="min-h-screen bg-brand-bg pb-20">
      <SEO title={career.title} description={career.tagline} image={ogImage} />
      
      <div className="relative h-[50vh] min-h-[400px] w-full bg-brand-dark overflow-hidden">
        <div className="absolute inset-0">
          <SmartImage 
            src={heroSrc} 
            alt={career.title}
            fallbackSrc={getRemoteFallback(career.title, 1200, 675)}
            width={1200}
            height={675}
            priority="high"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/80 to-transparent"></div>
        </div>

        <div className="absolute inset-0 flex flex-col justify-end pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link to="/careers" className="inline-flex items-center text-brand-grey2 hover:text-brand-cyan mb-6 transition-colors">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Careers
            </Link>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan text-xs font-semibold uppercase tracking-wider mb-4">
                  {career.category}
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-brand-white mb-4">
                  {career.title} <span className="text-4xl ml-2">{career.emoji}</span>
                </h1>
                <p className="text-xl text-brand-grey1 max-w-2xl font-light">{career.tagline}</p>
              </div>
              <div className="flex-shrink-0">
                {hasValidId ? (
                  <a href={utmWatchUrl!} target="_blank" rel="noreferrer" onClick={handleYoutubeClick}>
                    <Button size="lg" className="shadow-xl shadow-brand-cyan/20 animate-pulse-slow">
                      <Youtube className="w-5 h-5 mr-2" />
                      {career.video.ctaText || "Watch Short"}
                    </Button>
                  </a>
                ) : (
                  <div className="bg-brand-light/50 backdrop-blur border border-brand-border px-6 py-3 rounded-full flex items-center text-brand-grey2">
                    <Film className="w-4 h-4 mr-2" />
                    Video Coming Soon
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          <div className="bg-brand-light/90 backdrop-blur border border-brand-border p-6 rounded-xl text-center">
            <DollarSign className="w-5 h-5 mx-auto text-brand-cyan mb-2" />
            <p className="text-brand-white font-medium text-lg">{career.salaryRange || "Variable"}</p>
          </div>
          <div className="bg-brand-light/90 backdrop-blur border border-brand-border p-6 rounded-xl text-center">
            <GraduationCap className="w-5 h-5 mx-auto text-brand-green mb-2" />
            <p className="text-brand-white font-medium text-lg">{career.education[0]}</p>
          </div>
          <div className="bg-brand-light/90 backdrop-blur border border-brand-border p-6 rounded-xl text-center">
            <TrendingUp className="w-5 h-5 mx-auto text-purple-400 mb-2" />
            <p className="text-brand-white font-medium text-lg">Growth Path</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <section>
              <SectionTitle>What You Do</SectionTitle>
              <div className="space-y-4">
                {career.whatTheyDo.map((item, idx) => (
                  <div key={idx} className="bg-brand-light p-4 rounded-xl border border-brand-border/50 flex gap-4">
                     <span className="w-6 h-6 rounded-full bg-brand-cyan/20 flex items-center justify-center text-brand-cyan text-xs font-bold">{idx + 1}</span>
                     <p className="text-brand-grey1">{item}</p>
                  </div>
                ))}
              </div>
            </section>
            
            {career.teenStart && career.teenStart.length > 0 && (
              <section>
                <SectionTitle>How to Start (Ages 14-16)</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {career.teenStart.map((step, idx) => (
                    <div key={idx} className="bg-brand-dark/40 border border-brand-border p-5 rounded-xl">
                      <p className="text-brand-white text-sm leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
          <div className="lg:col-span-1">
             <div className="flex flex-col gap-4">
                <div className="aspect-[9/16] relative bg-brand-dark rounded-2xl overflow-hidden border border-brand-border group shadow-2xl">
                  {hasValidId && embedUrl ? (
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      title={`${career.title} Short`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : hasValidId ? (
                    <>
                      <SmartImage 
                        src={sidebarSrc} 
                        alt={`${career.title} Preview`}
                        fallbackSrc={getRemoteFallback(career.title, 720, 1280)}
                        width={720}
                        height={1280}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                      />
                      <div className="absolute inset-0 bg-brand-bg/20 group-hover:bg-brand-bg/10 transition-colors"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <a 
                          href={utmWatchUrl!} 
                          target="_blank" 
                          rel="noreferrer" 
                          onClick={handleYoutubeClick}
                          className="w-16 h-16 bg-brand-cyan rounded-full flex items-center justify-center text-brand-bg shadow-2xl transition-transform hover:scale-110 active:scale-95"
                        >
                          <Youtube className="w-8 h-8 fill-current" />
                        </a>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center bg-brand-dark/60">
                      <Film className="w-12 h-12 text-brand-border mb-4" />
                      <p className="text-brand-grey2 text-sm font-medium">Portrait video currently in production. Stay tuned!</p>
                    </div>
                  )}
                </div>
                
                {hasValidId && utmWatchUrl && (
                  <a href={utmWatchUrl} target="_blank" rel="noreferrer" className="block" onClick={handleYoutubeClick}>
                    <Button variant="secondary" fullWidth className="group">
                      Open on YouTube
                      <ExternalLink className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Button>
                  </a>
                )}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerDetailPage;
